import { Component } from 'react';

import { GoogleTagManager } from '@iwoca/frontend-tracking-library';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import messages from './ActionButtonMakeRepayment.messages';
import { getCountry } from '../../../../iwIntlProvider/CountryProvider';
import { FormattedCurrency } from '../../../../iwIntlProvider/FormattedCurrency';
import IwMedia from '../../../../iwMedia';
import { REQUEST_CHANGE_ROUTER } from '../../../../iwRouter/IwRouter.actions';
import { EarlyRepaymentSVG } from '../../../../svgComponents/earlyRepaymentSVG';
import { FailedSVG } from '../../../../svgComponents/failedSVG/index';
import { EARLY_REPAYMENT_ACCOUNT_ID } from '../../../earlyRepayment/EarlyRepayment.actions';
import { ActionButton } from '../../../ui/actionButton';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    addAccountIdToEarlyRepayment: (accountId: string) =>
        dispatch({
            type: EARLY_REPAYMENT_ACCOUNT_ID,
            accountId
        }),
    goToEarlyRepayment: () =>
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            location: { pathname: '/repayment/' }
        })
});

type PropsFromRedux = ReturnType<typeof mapDispatchToProps>;
type TProps = PropsFromRedux & {
    accountId: string;
    outstandingBalance: number;
    overduePaymentAmount?: number;
    isPaymentOverdue?: boolean;
    disabled?: boolean;
};

class ActionButtonMakeRepayment extends Component<TProps> {
    trackAndGoToEarlyRepayment = () => {
        GoogleTagManager.trackClick({
            identifier: 'Make early repayment button'
        });

        this.props.addAccountIdToEarlyRepayment(this.props.accountId);
        this.props.goToEarlyRepayment();
    };

    renderBalance() {
        const { outstandingBalance } = this.props;

        return (
            <FormattedCurrency value={outstandingBalance} decimalPlaces={2} />
        );
    }

    renderOverdueAmount() {
        const { overduePaymentAmount } = this.props;

        return (
            <FormattedCurrency
                value={overduePaymentAmount!}
                decimalPlaces={2}
            />
        );
    }

    renderMessage() {
        const { outstandingBalance, isPaymentOverdue } = this.props;
        const balance = this.renderBalance();
        const overdueAmount = this.renderOverdueAmount();

        if (isPaymentOverdue) {
            return (
                <FormattedMessage
                    {...messages.overdueCaption}
                    values={{ overdueAmount }}
                />
            );
        } else if (outstandingBalance > 0) {
            return (
                <FormattedMessage {...messages.caption} values={{ balance }} />
            );
        } else {
            return (
                <FormattedMessage
                    {...messages.futureCaption}
                    values={{ balance }}
                />
            );
        }
    }

    renderLabel() {
        const { isPaymentOverdue } = this.props;

        if (isPaymentOverdue) {
            return (
                <>
                    <FormattedMessage {...messages.overdueLabel} />
                </>
            );
        }

        return (
            <>
                <IwMedia to="small">
                    <FormattedMessage {...messages.shortLabel} />
                </IwMedia>
                <IwMedia from="small">
                    <FormattedMessage {...messages.label} />
                </IwMedia>
            </>
        );
    }

    renderCoronaBody() {
        return (
            <div data-testid="coronavirus-info-body">
                <FormattedMessage
                    {...messages.coronaSubTitle}
                    values={{
                        link: (
                            <a
                                href="https://support.iwoca.co.uk/en/articles/1608757-what-to-do-if-you-can-t-make-your-repayment"
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                <FormattedMessage
                                    {...messages.coronaWaysWeCanHelp}
                                />
                            </a>
                        ),
                        phoneNumber: (
                            <FormattedMessage
                                tagName="strong"
                                {...messages.coronaPhoneNumber}
                            />
                        )
                    }}
                />
            </div>
        );
    }

    render() {
        const { disabled } = this.props;
        const message = this.renderMessage();
        const label = this.renderLabel();
        const coronaBody = this.renderCoronaBody();

        return (
            <>
                <ActionButton
                    label={label}
                    actionIcon={EarlyRepaymentSVG}
                    disabled={disabled}
                    caption={message}
                    showIcon={!disabled}
                    onClick={this.trackAndGoToEarlyRepayment}
                    data-testid="make-repayment-action"
                    data-hoverid="make-repayment-action"
                />
                {getCountry() === 'UK' && (
                    <ActionButton
                        label={<FormattedMessage {...messages.coronaTitle} />}
                        actionIcon={FailedSVG}
                        caption={coronaBody}
                        showIcon={!disabled}
                    />
                )}
            </>
        );
    }
}

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(ActionButtonMakeRepayment);
